import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import {
  getSalesDocs, issueCreditNote, issueDebitNote, issueSalesDoc, voidSalesDoc,
  downloadSalesDocPdf, downloadSalesDocWhtCertificate, getTenantCurrency,
} from "../api";
import { exportSalesDocsCsv } from "./SalesDocShared";

const NAVY = "#0f172a";
const FINANCE_PRIMARY = "#1e3a8a";

type NoteType = "credit_note" | "debit_note";

const TYPE_LABELS: Record<NoteType, { tab: string; singular: string }> = {
  credit_note: { tab: "Credit Notes", singular: "Credit Note" },
  debit_note:  { tab: "Debit Notes",  singular: "Debit Note" },
};

const REASONS: Record<NoteType, string[]> = {
  credit_note: ["Return of goods", "Price adjustment", "Billing error", "Discount", "Other"],
  debit_note:  ["Additional charges", "Price adjustment", "Undercharge correction", "Other"],
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  draft: { label: "Draft",  bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  sent:  { label: "Issued", bg: "#dcfce7", text: "#166534", border: "#86efac" },
  void:  { label: "Void",   bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
};

const BLANK_ITEM = { description: "", quantity: "1", unit_price: "", amount: "" };

function blankForm(type: NoteType, tiId?: number) {
  return {
    type,
    reference_ti_id: tiId ? String(tiId) : "",
    reason: "",
    reason_detail: "",
    issue_date: new Date().toISOString().split("T")[0],
    line_items: [{ ...BLANK_ITEM }],
  };
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

export default function SalesCreditDebitNotesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();

  const [tab, setTab] = useState<NoteType>("credit_note");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm(tab));
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [tiPickerOpen, setTiPickerOpen] = useState(false);
  const [tiQuery, setTiQuery] = useState("");
  const [downloading, setDownloading] = useState<number | null>(null);

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["sales-docs", tab, statusFilter],
    queryFn: () => getSalesDocs({ doc_type: tab, status: statusFilter || undefined }),
  });

  // Tax invoices are only eligible once sent or paid — fetch unfiltered so
  // paid TIs (which double as receipts and are excluded from the doc_type=tax_invoice
  // list) are still available as a reference source.
  const { data: allDocs = [] } = useQuery({
    queryKey: ["sales-docs-all-for-notes"],
    queryFn: () => getSalesDocs({}),
    staleTime: 60_000,
  });
  const { data: tenantCurrency } = useQuery({
    queryKey: ["tenant-currency"],
    queryFn: getTenantCurrency,
    staleTime: 5 * 60_000,
  });
  const eligibleTis = useMemo(
    () => (allDocs as any[]).filter(d => d.doc_type === "tax_invoice" && (d.status === "sent" || d.status === "paid")),
    [allDocs]
  );
  const selectedTi = useMemo(
    () => (allDocs as any[]).find(d => String(d.id) === String(form.reference_ti_id)),
    [allDocs, form.reference_ti_id]
  );

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return (notes as any[]).filter((n: any) =>
      (n.doc_number || "").toLowerCase().includes(q) ||
      (n.references_doc_number || "").toLowerCase().includes(q) ||
      (n.client_name || "").toLowerCase().includes(q)
    );
  }, [notes, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["sales-docs"] });

  const createMut = useMutation({
    mutationFn: ({ tiId, type, data }: { tiId: number; type: NoteType; data: object }) =>
      type === "credit_note" ? issueCreditNote(tiId, data) : issueDebitNote(tiId, data),
    onSuccess: () => { invalidate(); closeForm(); toast(`${TYPE_LABELS[form.type].singular} created as draft`, "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to create note", "error"),
  });
  const issueMut = useMutation({
    mutationFn: (id: number) => issueSalesDoc(id),
    onSuccess: () => { invalidate(); toast("Note issued", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to issue note", "error"),
  });
  const voidMut = useMutation({
    mutationFn: (id: number) => voidSalesDoc(id),
    onSuccess: () => { invalidate(); toast("Note voided", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to void note", "error"),
  });

  function closeForm() {
    setShowForm(false);
    setTriedSubmit(false);
    setTiQuery("");
    setForm(blankForm(tab));
  }

  function startCreate() {
    setForm(blankForm(tab));
    setTriedSubmit(false);
    setTiQuery("");
    setShowForm(true);
  }

  const updateLineItem = (i: number, field: string, value: string) => {
    setForm(f => {
      const items = [...f.line_items];
      items[i] = { ...items[i], [field]: value };
      if (field === "quantity" || field === "unit_price") {
        const qty = parseFloat(field === "quantity" ? value : items[i].quantity) || 0;
        const price = parseFloat(field === "unit_price" ? value : items[i].unit_price) || 0;
        items[i].amount = (qty * price).toFixed(2);
      }
      return { ...f, line_items: items };
    });
  };
  const addLineItem = () => setForm(f => ({ ...f, line_items: [...f.line_items, { ...BLANK_ITEM }] }));
  const removeLineItem = (i: number) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const taxRate = selectedTi?.tax_rate ?? 7;
  const calcSubtotal = () => round2(form.line_items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0));
  const calcTax = () => round2(calcSubtotal() * (taxRate / 100));
  const calcTotal = () => round2(calcSubtotal() + calcTax());

  const errors: { reference_ti_id?: string; reason?: string; reason_detail?: string; issue_date?: string; line_items?: string } = {};
  if (!form.reference_ti_id) errors.reference_ti_id = "Select the tax invoice this note is issued against";
  if (!form.reason) errors.reason = "Select a reason";
  if (form.reason === "Other" && !form.reason_detail.trim()) errors.reason_detail = "Describe the reason";
  if (!form.issue_date) errors.issue_date = "Issue date is required";
  if (!form.line_items.some(it => it.description.trim())) errors.line_items = "Add at least one line item with a description";
  const hasErrors = Object.keys(errors).length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTriedSubmit(true);
    if (hasErrors) return;
    const payload = {
      reason: form.reason,
      reason_detail: form.reason === "Other" ? form.reason_detail : undefined,
      issue_date: form.issue_date,
      line_items: form.line_items
        .filter(it => it.description.trim())
        .map(it => ({
          description: it.description, quantity: parseFloat(it.quantity) || 0,
          unit_price: parseFloat(it.unit_price) || 0, amount: parseFloat(it.amount) || 0,
        })),
    };
    createMut.mutate({ tiId: parseInt(form.reference_ti_id, 10), type: form.type, data: payload });
  }

  async function handleDownload(note: any) {
    setDownloading(note.id);
    try {
      const { blob, filename } = await downloadSalesDocPdf(note.id, note.doc_number);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Failed to download PDF", "error");
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadWht(note: any) {
    setDownloading(note.id);
    try {
      const { blob, filename } = await downloadSalesDocWhtCertificate(note.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Failed to download WHT certificate", "error");
    } finally {
      setDownloading(null);
    }
  }

  // Before a source tax invoice is picked, fall back to the tenant's own
  // configured currency rather than a hardcoded Baht symbol (bug-33/34/35) —
  // "฿" stays only as the last-resort default, matching SalesDocShared.tsx.
  const sym = selectedTi?.currency_symbol || (tenantCurrency as any)?.currency_symbol || "฿";

  return (
    <div>
      <LoadingOverlay active={createMut.isPending || issueMut.isPending || voidMut.isPending || downloading !== null} />
      <div className="page-header">
        <div>
          <h1>Credit / Debit Notes</h1>
          <p>
            AR adjustments against a sent or paid Tax Invoice
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => exportSalesDocsCsv(tab, filteredNotes)} style={outlineBtnStyle}>↓ Export CSV</button>
          <button onClick={() => { showForm ? closeForm() : startCreate(); }} style={primaryBtnStyle}>
            {showForm ? "Cancel" : `+ New ${TYPE_LABELS[tab].singular}`}
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid #e2e8f0" }}>
        {(["credit_note", "debit_note"] as NoteType[]).map(t => (
          <button key={t} onClick={() => { setTab(t); setShowForm(false); setStatusFilter(""); }} style={{
            padding: "10px 18px", background: "none", border: "none",
            borderBottom: tab === t ? `2px solid ${FINANCE_PRIMARY}` : "2px solid transparent",
            color: tab === t ? FINANCE_PRIMARY : "#64748b", fontWeight: 600, fontSize: 14, cursor: "pointer",
          }}>{TYPE_LABELS[t].tab}</button>
        ))}
      </div>

      {showForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 16 }}>New {TYPE_LABELS[tab].singular}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div style={{ position: "relative" }}>
                <label style={labelStyle}>Reference Tax Invoice <span style={requiredMarkStyle}>*</span></label>
                <input
                  placeholder="Type to search TI no. or customer…"
                  value={tiPickerOpen ? tiQuery : (selectedTi ? `${selectedTi.doc_number} — ${selectedTi.client_name}` : "")}
                  onFocus={() => { setTiPickerOpen(true); setTiQuery(""); }}
                  onChange={e => setTiQuery(e.target.value)}
                  onBlur={() => setTimeout(() => setTiPickerOpen(false), 160)}
                  style={inputStyle}
                />
                {tiPickerOpen && (() => {
                  const q = tiQuery.toLowerCase();
                  const hits = eligibleTis.filter((d: any) =>
                    !q || d.doc_number.toLowerCase().includes(q) || (d.client_name || "").toLowerCase().includes(q)
                  ).slice(0, 8);
                  return (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", marginTop: 2, maxHeight: 240, overflowY: "auto" }}>
                      {hits.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8" }}>No sent/paid tax invoices match</div>}
                      {hits.map((d: any) => (
                        <button key={d.id} type="button"
                          onMouseDown={() => { setForm(f => ({ ...f, reference_ti_id: String(d.id) })); setTiPickerOpen(false); }}
                          style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 12px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                          onMouseLeave={e => (e.currentTarget.style.background = "none")}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{d.doc_number}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{d.client_name} · {d.issue_date}</div>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#0f766e", whiteSpace: "nowrap" }}>{d.currency_symbol}{d.total?.toLocaleString("en", { minimumFractionDigits: 2 })}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {triedSubmit && errors.reference_ti_id && <div style={errorTextStyle}>{errors.reference_ti_id}</div>}
              </div>
              <div>
                <label style={labelStyle}>Reason <span style={requiredMarkStyle}>*</span></label>
                <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={inputStyle}>
                  <option value="">Select reason…</option>
                  {REASONS[tab].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {triedSubmit && errors.reason && <div style={errorTextStyle}>{errors.reason}</div>}
              </div>
              <div>
                <label style={labelStyle}>Issue Date <span style={requiredMarkStyle}>*</span></label>
                <input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} style={inputStyle} />
                {triedSubmit && errors.issue_date && <div style={errorTextStyle}>{errors.issue_date}</div>}
              </div>
              {form.reason === "Other" && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Reason Detail <span style={requiredMarkStyle}>*</span></label>
                  <input placeholder="Describe the reason…" value={form.reason_detail}
                    onChange={e => setForm(f => ({ ...f, reason_detail: e.target.value }))} style={inputStyle} />
                  {triedSubmit && errors.reason_detail && <div style={errorTextStyle}>{errors.reason_detail}</div>}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>Line Items <span style={requiredMarkStyle}>*</span></label>
                <button type="button" onClick={addLineItem} style={outlineBtnStyle}>+ Add Item</button>
              </div>
              {triedSubmit && errors.line_items && <div style={errorTextStyle}>{errors.line_items}</div>}
              <div className="table-wrap">
                <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1.5fr 1.5fr auto", gap: 8, marginBottom: 6, minWidth: 480 }}>
                  {["Description", "Qty", `Unit Price (${sym})`, `Amount (${sym})`, ""].map(h => (
                    <div key={h} style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{h}</div>
                  ))}
                </div>
                {form.line_items.map((item, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1.5fr 1.5fr auto", gap: 8, marginBottom: 8, minWidth: 480 }}>
                    <input placeholder="Description" value={item.description}
                      onChange={e => updateLineItem(i, "description", e.target.value)} style={inputStyle} />
                    <input type="number" placeholder="1" value={item.quantity} min={0} step="0.01"
                      onChange={e => updateLineItem(i, "quantity", e.target.value)} style={inputStyle} />
                    <input type="number" placeholder="0.00" value={item.unit_price} min={0} step="0.01"
                      onChange={e => updateLineItem(i, "unit_price", e.target.value)} style={inputStyle} />
                    <input type="number" placeholder="0.00" value={item.amount} min={0} step="0.01"
                      onChange={e => updateLineItem(i, "amount", e.target.value)} style={inputStyle} />
                    <button type="button" onClick={() => removeLineItem(i)} disabled={form.line_items.length === 1}
                      style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, cursor: form.line_items.length === 1 ? "not-allowed" : "pointer", padding: "0 10px", opacity: form.line_items.length === 1 ? 0.4 : 1 }}>✕</button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <div style={{ fontSize: 13, color: "#64748b" }}>Subtotal: <strong>{sym}{calcSubtotal().toFixed(2)}</strong></div>
                <div style={{ fontSize: 13, color: "#64748b" }}>Tax ({taxRate}%): <strong>{sym}{calcTax().toFixed(2)}</strong></div>
                <div style={{ fontSize: 15, color: NAVY, fontWeight: 700 }}>Total: {sym}{calcTotal().toFixed(2)}</div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" onClick={closeForm} style={outlineBtnStyle}>Cancel</button>
              <button type="submit" disabled={createMut.isPending}
                title={hasErrors ? "Fill in the required fields to continue" : undefined}
                style={{ ...primaryBtnStyle, opacity: createMut.isPending ? 0.6 : 1 }}>
                {createMut.isPending ? "Saving…" : "Save Draft"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Status filters ── */}
      <div className="filter-bar" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {[["", "All"], ["draft", "Draft"], ["sent", "Issued"], ["void", "Void"]].map(([s, label]) => (
          <button key={s || "all"} onClick={() => setStatusFilter(s)} style={{
            padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0",
            background: statusFilter === s ? FINANCE_PRIMARY : "white",
            color: statusFilter === s ? "white" : "#64748b",
            cursor: "pointer", fontSize: 13, fontWeight: 500,
          }}>{label}</button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search document no., tax invoice, or customer…"
          style={{ ...inputStyle, maxWidth: 300, padding: "7px 12px" }} />
        {search && <button onClick={() => setSearch("")} style={{ ...outlineBtnStyle, padding: "6px 10px", color: "#94a3b8" }}>✕</button>}
      </div>

      {isLoading && <p style={{ color: "#64748b" }}>Loading...</p>}

      <div className="table-wrap" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              {["Document No.", "Reference TI", "Customer", "Reason", "Total", "Status", "Issue Date", ""].map(h => (
                <th key={h} style={{ padding: "10px 16px", textAlign: h === "Total" ? "right" : "left", fontWeight: 600, color: "#475569", fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!isLoading && filteredNotes.length === 0 && (
              <tr><td colSpan={8} style={{ padding: "24px 16px", textAlign: "center", color: "#94a3b8" }}>No {TYPE_LABELS[tab].tab.toLowerCase()} found</td></tr>
            )}
            {filteredNotes.map((note: any) => {
              const sc = STATUS_CONFIG[note.status] || STATUS_CONFIG.draft;
              const reason = (note.notes || "").replace(/^Reason:\s*/, "");
              return (
                <tr key={note.id} style={{ borderBottom: "1px solid #f1f5f9" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                    onMouseLeave={e => (e.currentTarget.style.background = "white")}>
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>{note.doc_number}</td>
                  <td style={{ padding: "12px 16px", color: "#1e293b" }}>{note.references_doc_number || "—"}</td>
                  <td style={{ padding: "12px 16px", color: "#1e293b" }}>{note.client_name || "—"}</td>
                  <td style={{ padding: "12px 16px", color: "#64748b" }}>{reason || "—"}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, color: FINANCE_PRIMARY, whiteSpace: "nowrap" }}>
                    {note.currency_symbol}{note.total?.toLocaleString("en", { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{sc.label}</span>
                  </td>
                  <td style={{ padding: "12px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{note.issue_date}</td>
                  <td style={{ padding: "8px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {note.status === "draft" && (
                        <AsyncButton onClick={async () => { if (await confirm(`Issue ${note.doc_number}? It will lock and become permanent.`, { confirmLabel: "Issue" })) await issueMut.mutateAsync(note.id); }}
                          style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          Issue
                        </AsyncButton>
                      )}
                      {note.status !== "void" && (
                        <AsyncButton onClick={async () => { if (await confirm(`Void ${note.doc_number}? This cannot be undone.`, { confirmLabel: "Void" })) await voidMut.mutateAsync(note.id); }}
                          style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          Void
                        </AsyncButton>
                      )}
                      {note.wht_amount > 0 && (
                        <AsyncButton onClick={() => handleDownloadWht(note)} title="Download WHT Certificate"
                          style={{ background: "#fefce8", border: "1px solid #fde047", color: "#854d0e", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          WHT Cert
                        </AsyncButton>
                      )}
                      <AsyncButton onClick={() => handleDownload(note)} title="Download PDF"
                        style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                        {downloading === note.id ? "…" : "⬇ PDF"}
                      </AsyncButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: FINANCE_PRIMARY,
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const errorTextStyle: React.CSSProperties = {
  color: "#dc2626", fontSize: 12, marginTop: 4,
};
const requiredMarkStyle: React.CSSProperties = {
  color: "#dc2626",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
